/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useEffect, useState } from 'react';

import { useIntl } from 'react-intl';

import { cssMap, jsx } from '@atlaskit/css';
import Modal, { ModalBody, ModalHeader, ModalTitle, ModalTransition } from '@atlaskit/modal-dialog';
import { Box, Text } from '@atlaskit/primitives/compiled';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';
import { logException } from '@atlassian/conversation-assistant-instrumentation/services/sentry';

import { isTextFile } from '../../utils/fileTypeHelpers';

import messages from './messages';

type FilePreviewModalProps = {
	isOpen: boolean;
	onClose: () => void;
	fileName: string;
	fileObject?: File;
	previewBlobUrl?: string;
	/**
	 * Text content (loaded before modal opens)
	 */
	textContent?: string | null;
	/**
	 * Whether text content is still loading
	 */
	isLoadingText?: boolean;
};

const styles = cssMap({
	imagePreview: {
		maxWidth: '100%',
		maxHeight: '80vh',
		objectFit: 'contain',
		margin: '0 auto',
		display: 'block',
	},
	pdfPreview: {
		width: '100%',
		height: '80vh',
		border: 'none',
		display: 'block',
	},
	loadingContainer: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		minHeight: '400px',
	},
	textPreview: {
		maxHeight: '80vh',
		overflow: 'auto',
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		backgroundColor: token('color.background.neutral'),
		borderRadius: token('radius.small'),
		font: token('font.code'),
		whiteSpace: 'pre-wrap',
		wordBreak: 'break-word',
	},
});

/**
 * Determines file type category from MIME type or file name
 */
const getFileType = (file: File): 'image' | 'pdf' | 'text' | 'unknown' => {
	if (file.type.startsWith('image/')) {
		return 'image';
	}
	if (file.type === 'application/pdf') {
		return 'pdf';
	}
	if (isTextFile(file.type, file.name)) {
		return 'text';
	}
	return 'unknown';
};

/**
 * Modal component for previewing images, PDFs, and text files.
 */
export const FilePreviewModal = ({
	isOpen,
	onClose,
	fileName,
	fileObject,
	previewBlobUrl,
	textContent,
	isLoadingText,
}: FilePreviewModalProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const fileType = fileObject ? getFileType(fileObject) : previewBlobUrl ? 'image' : 'unknown';
	const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);

	// Create PDF blob URL and track errors
	useEffect(() => {
		if (fileType === 'pdf' && fileObject) {
			try {
				const blobUrl = URL.createObjectURL(fileObject);
				setPdfBlobUrl(blobUrl);
			} catch (error) {
				// Log error to Sentry for monitoring
				logException({
					exception: error,
					name: 'pdf-blob-url-creation-error',
					tags: {
						fileType: fileObject.type,
					},
				});
				// User will see "Preview not available" message
				setPdfBlobUrl(null);
			}
		} else {
			setPdfBlobUrl(null);
		}
	}, [fileType, fileObject]);

	// Cleanup PDF blob URL on unmount
	useEffect(() => {
		return () => {
			if (pdfBlobUrl) {
				URL.revokeObjectURL(pdfBlobUrl);
			}
		};
	}, [pdfBlobUrl]);

	const renderPreview = () => {
		if (fileType === 'image' && previewBlobUrl) {
			return <Box as="img" src={previewBlobUrl} alt={fileName} xcss={styles.imagePreview} />;
		}

		if (fileType === 'pdf' && pdfBlobUrl) {
			return (
				<Box
					as="object"
					type="application/pdf"
					data={`${pdfBlobUrl}#toolbar=1`}
					title={fileName}
					xcss={styles.pdfPreview}
				>
					<Box
						as="embed"
						type="application/pdf"
						src={`${pdfBlobUrl}#toolbar=1`}
						xcss={styles.pdfPreview}
					/>
				</Box>
			);
		}

		if (fileType === 'text') {
			if (isLoadingText) {
				return (
					<Box xcss={styles.loadingContainer}>
						<Spinner size="large" />
					</Box>
				);
			}
			return <Box xcss={styles.textPreview}>{textContent}</Box>;
		}

		return (
			<Box xcss={styles.loadingContainer}>
				<Text>{formatMessage(messages.previewNotAvailable)}</Text>
			</Box>
		);
	};

	return (
		<ModalTransition>
			{isOpen && (
				<Modal onClose={onClose} width="x-large" shouldScrollInViewport>
					<ModalHeader hasCloseButton>
						<ModalTitle>{fileName}</ModalTitle>
					</ModalHeader>
					<ModalBody hasInlinePadding={false}>{renderPreview()}</ModalBody>
				</Modal>
			)}
		</ModalTransition>
	);
};
